import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Tab, TabPanel, Tabs, IconButton } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { useExceededLimitation } from "app/components/metering/UseLimitation";
import { HOOKS_ICON } from "app/constants/hooks";
import type { ICustomSegment, ICustomSegmentAccount } from "app/services/segments/segmentsApiService";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { WebsiteSegmentsTable } from "../../analysis/components/WebsiteSegmentsTable";
import { ContentContainer, TabListStyled, TabsContainer } from "../../components/SegmentsSharedStyledComponents";
import { isSegmentsLimitationExceeded, showSegmentsMeteringHook } from "../../metering/SegmentsMetering";
import { SegmentsTableStyles } from "../../styleComponents";

import { ETab } from "./types";
import { allTrackers } from "app/services/track/track";

export const getTabNameForTracking = ({ selectedSegmentTab }) => {
    switch (selectedSegmentTab) {
        case ETab.SHARED_SEGMENTS.toString():
            return "Shared segments";
        default:
            return "My segments";
    }
};

interface ISegmentTableTabs {
    segments: ICustomSegment[];
    organizationSegments: ICustomSegmentAccount[];
    params: {
        tab?: string;
    };
    isLoading: boolean;
}

export const SegmentTableTabs: FC<ISegmentTableTabs> = ({
    params,
    isLoading,
    segments = [],
    organizationSegments = [],
}) => {
    const t = useTranslation();
    const tabValues = Object.values(ETab).filter((value) => typeof value === "number") as number[];
    const apiParams = proNavigator.getApiParams() as { tab?: string | number };
    const tabParam = Number(apiParams.tab);
    const initialTab = tabValues.includes(tabParam) ? tabParam : ETab.MY_SEGMENTS;
    const [selectedTab, setSelectedTab] = useState<ETab>(initialTab);

    const isLimitationExceeded = useExceededLimitation(isSegmentsLimitationExceeded);

    const createSegment = () => {
        if (isLimitationExceeded) {
            showSegmentsMeteringHook();
            return;
        }

        allTrackers.dispatchTrackEvent("Button", "click", "segmentAnalysis/create new segment");
        const currentModule = proNavigator.getCurrentModule();
        const segmentWizardStateName = `${currentModule}-wizard`;
        void proNavigator.go(segmentWizardStateName);
    };

    useEffect(() => {
        const validTabs = [ETab.MY_SEGMENTS, ETab.SHARED_SEGMENTS];
        const nextTab = Number(params.tab);
        if (params.tab && validTabs.includes(nextTab)) {
            setSelectedTab(nextTab);
        }
    }, [params.tab]);

    const changeTab = useCallback((index: number) => {
        setSelectedTab(index);
        const navigatorParams = proNavigator.getParams();
        void proNavigator.updateParams({ ...navigatorParams, tab: index });
    }, []);

    const onTabSelect = (index: number) => {
        changeTab(index);
        allTrackers.dispatchTrackEvent(
            "Tab",
            "switch",
            `segment/home/tabs/switch/${getTabNameForTracking({ selectedSegmentTab: index.toString() }) || ""}`,
        );
    };

    const [allowedOrganizationSegments, allowedSegments] = useMemo(() => {
        const allowedSegmentFilter = (seg: ICustomSegment | ICustomSegmentAccount) => {
            if ("isLocked" in seg) {
                return !seg.isLocked;
            }
            return true;
        };

        return [organizationSegments.filter(allowedSegmentFilter), segments.filter(allowedSegmentFilter)];
    }, [organizationSegments, segments]);

    const tabsData = {
        [ETab.MY_SEGMENTS]: allowedSegments,
        [ETab.SHARED_SEGMENTS]: allowedOrganizationSegments,
    };
    const mySegmentsCount = tabsData[ETab.MY_SEGMENTS]?.length ?? 0;
    const sharedSegmentsCount = tabsData[ETab.SHARED_SEGMENTS]?.length ?? 0;

    return (
        <>
            <TabsContainer>
                <TabListStyled>
                    <Tab
                        selected={selectedTab === ETab.MY_SEGMENTS}
                        className="firstTab"
                        onClick={() => onTabSelect(ETab.MY_SEGMENTS)}
                        key="tab-0"
                    >
                        {t("segments.analysis.my.segment")} ({mySegmentsCount})
                    </Tab>
                    <Tab
                        selected={selectedTab === ETab.SHARED_SEGMENTS}
                        className="secondTab"
                        onClick={() => onTabSelect(ETab.SHARED_SEGMENTS)}
                        key="tab-1"
                    >
                        {t("segments.analysis.shared.segments")} ({sharedSegmentsCount})
                    </Tab>
                </TabListStyled>
                <IconButton
                    iconName={isLimitationExceeded ? HOOKS_ICON : "add"}
                    onClick={createSegment}
                    type="outlined"
                >
                    {t("segments.analysis.button.new.segment")}
                </IconButton>
            </TabsContainer>
            <ContentContainer>
                <SegmentsTableStyles />
                <Tabs
                    forceRenderTabPanel={false}
                    onSelect={onTabSelect}
                    selectedIndex={selectedTab}
                >
                    <TabPanel className="firstTab">
                        <div>
                            <WebsiteSegmentsTable
                                hasOwnerActions
                                isLoading={isLoading}
                                isLimitationExceeded={isLimitationExceeded}
                                showOwnerColumn={false}
                                tableSelectionKey="MySegmentsTable"
                                tableData={tabsData[ETab.MY_SEGMENTS]}
                                noData={{
                                    titleKey: "segment.home.mysegments.table.no.data.title",
                                    subtitleKey: "segment.home.mysegments.table.no.data.subtitle",
                                }}
                            />
                        </div>
                    </TabPanel>
                    <TabPanel className="secondTab">
                        <div>
                            <WebsiteSegmentsTable
                                showOwnerColumn
                                withComparisonSelection
                                isLoading={isLoading}
                                isLimitationExceeded={isLimitationExceeded}
                                tableData={tabsData[ETab.SHARED_SEGMENTS]}
                                tableSelectionKey="OrgSegmentsTable"
                                noData={{
                                    subtitleKey: "global.nodata.notavilable",
                                }}
                            />
                        </div>
                    </TabPanel>
                </Tabs>
            </ContentContainer>
        </>
    );
};
